var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _SimpleData_instances, _SimpleData_updateSimpleData;
import { addBins, addOutliers, addProportions, addQuantiles, addRank, addVariation, correlation, describe, excludeOutliers, regression, sortValues, summarize, } from "../exports/analyzing.js";
import { checkValues, datesToString, excludeMissingValues, formatAllKeys, modifyItems, modifyValues, removeDuplicates, renameKey, replaceValues, roundValues, valuesToDate, valuesToFloat, valuesToInteger, valuesToString, } from "../exports/cleaning.js";
import { getDataAsArrays, getMax, getMean, getMedian, getMin, getQuantile, getSum, getUniqueValues, getArray, getItem, } from "../exports/exporting.js";
import { arraysToData, asyncLogCall, cloneData, handleMissingKeys, log, logCall, round, showTable, } from "../exports/helpers.js";
import { loadDataFromUrl } from "../exports/importing.js";
import { addItems, addKey, keysToValues, mergeItems, removeKey, valuesToKeys, } from "../exports/restructuring.js";
import { exclude, filterItems, filterValues, keep, keepDates, keepNumbers, keepStrings, pickRandomItems, selectKeys, } from "../exports/selecting.js";
import { getChart, getCustomChart } from "../exports/visualizing.js";
/**
 * SimpleData usage example.
 *
 * ```typescript
 * const data = [{ key: value }, ...]
 * const simpleData = new SimpleData({ data: data })
 * ```
 */
export default class SimpleData {
    /**
     * SimpleData constructor
     * @param __namedParameters.data  Data as a list of objects with the same keys.
     * @param __namedParameters.verbose  Log information in the console on `SimpleData` method calls.
     * @param __namedParameters.nbTableItemsToLog  Number of items to log in table. Only applies when `verbose` is true.
     * @param __namedParameters.fillMissingKeys  Fill missing keys with `undefined`.
     */
    constructor({ data = [], dataAsArrays = false, verbose = false, nbTableItemsToLog = 5, fillMissingKeys = false, firstItem = 0, lastItem = Infinity, duration = 0, } = {}) {
        _SimpleData_instances.add(this);
        if (data.length > 0 || Object.keys(data).length > 0) {
            const incomingData = dataAsArrays
                ? cloneData(arraysToData(data, verbose).slice(firstItem, lastItem + 1))
                : cloneData(data.slice(firstItem, lastItem + 1));
            handleMissingKeys(incomingData, fillMissingKeys, undefined, undefined, verbose);
            this._data = incomingData;
        }
        else {
            verbose && log("\nnew SimpleData()\nStarting an empty SimpleData");
            this._data = [];
        }
        this.duration = duration;
        this.verbose = verbose;
        this.nbTableItemsToLog = nbTableItemsToLog;
    }
    // *** IMPORTING METHOD *** //
    loadDataFromUrl({ url, autoType = false, missingKeyValues = { null: null, NaN: NaN, undefined: undefined }, fillMissingKeys = false, fileNameAsId = false, dataAsArrays = false, firstItem = 0, lastItem = Infinity, nbFirstRowsToExclude = 0, nbLastRowsToExclude = Infinity, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._data.length > 0) {
                throw new Error("This SimpleData already has data. Create another one.");
            }
            const data = yield loadDataFromUrl(url, autoType, dataAsArrays, firstItem, lastItem, nbFirstRowsToExclude, nbLastRowsToExclude, fillMissingKeys, fileNameAsId, missingKeyValues, this.verbose);
            __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, data);
            return this;
        });
    }
    // CLEANING METHODS //
    describe() {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, describe(cloneData(this._data)));
        return this;
    }
    checkValues({ nbItemsToCheck = "all", randomize = false, } = {}) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, checkValues(cloneData(this._data), nbItemsToCheck, randomize));
        return this;
    }
    excludeMissingValues({ key, missingValues, keepExcludedOnly = false, } = {}) {
        if (missingValues === undefined) {
            missingValues = [null, NaN, undefined, ""];
        }
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, excludeMissingValues(cloneData(this._data), key, missingValues, this.verbose, keepExcludedOnly));
        return this;
    }
    formatAllKeys() {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, formatAllKeys(cloneData(this._data), this.verbose));
        return this;
    }
    renameKey({ oldKey, newKey }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, renameKey(cloneData(this._data), oldKey, newKey));
        return this;
    }
    valuesToString({ key, newKey }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, valuesToString(cloneData(this._data), key, newKey));
        return this;
    }
    valuesToInteger({ key, thousandSeparator = ",", decimalSeparator = ".", skipErrors = false, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, valuesToInteger(cloneData(this._data), key, thousandSeparator, decimalSeparator, skipErrors, newKey));
        return this;
    }
    valuesToFloat({ key, thousandSeparator = ",", decimalSeparator = ".", skipErrors = false, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, valuesToFloat(cloneData(this._data), key, thousandSeparator, decimalSeparator, skipErrors, newKey));
        return this;
    }
    valuesToDate({ key, format, skipErrors = false, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, valuesToDate(cloneData(this._data), key, format, skipErrors, newKey));
        return this;
    }
    datesToString({ key, format, skipErrors = false, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, datesToString(cloneData(this._data), key, format, skipErrors, newKey));
        return this;
    }
    roundValues({ key, nbDigits = 1, skipErrors = false, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, roundValues(cloneData(this._data), key, nbDigits, skipErrors, newKey));
        return this;
    }
    replaceValues({ key, oldValue, newValue, method = undefined, skipErrors = false, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, replaceValues(cloneData(this._data), key, oldValue, newValue, method, skipErrors, newKey));
        return this;
    }
    modifyValues({ key, valueGenerator, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, modifyValues(cloneData(this._data), key, valueGenerator, newKey));
        return this;
    }
    modifyItems({ key, itemGenerator, newKey, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, modifyItems(cloneData(this._data), key, itemGenerator, newKey));
        return this;
    }
    excludeOutliers({ key, nbTestedValues = 10000, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, excludeOutliers(cloneData(this._data), key, nbTestedValues, this.verbose));
        return this;
    }
    // *** RESTRUCTURING METHODS *** //
    removeKey({ key }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, removeKey(cloneData(this._data), key));
        return this;
    }
    addKey({ key, itemGenerator, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addKey(cloneData(this._data), key, itemGenerator));
        return this;
    }
    addRank({ newKey, key, sortInPlace, order, locale, handleTies, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addRank(cloneData(this._data), newKey, key, sortInPlace, order, handleTies, locale));
        return this;
    }
    addItems({ dataToBeAdded, fillMissingKeys = false, defaultValue = undefined, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addItems(cloneData(this._data), dataToBeAdded, fillMissingKeys, defaultValue, this.verbose));
        return this;
    }
    mergeItems({ dataToBeMerged, commonKey, nbTestedValues = 10000, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, mergeItems(cloneData(this._data), dataToBeMerged, commonKey, this.verbose, nbTestedValues));
        return this;
    }
    valuesToKeys({ newKeys, newValues, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, valuesToKeys(cloneData(this._data), newKeys, newValues, this.verbose));
        return this;
    }
    keysToValues({ keys, newKeyForKeys, newKeyForValues, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, keysToValues(cloneData(this._data), keys, newKeyForKeys, newKeyForValues, this.verbose));
        return this;
    }
    //*** SELECTION METHODS ***/
    selectKeys({ keys }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, selectKeys(cloneData(this._data), keys));
        return this;
    }
    filterValues({ key, valueComparator, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, filterValues(cloneData(this._data), key, valueComparator, this.verbose));
        return this;
    }
    filterItems({ itemComparator, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, filterItems(cloneData(this._data), itemComparator, this.verbose));
        return this;
    }
    pickRandomItems({ nbItems, seed, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, pickRandomItems(cloneData(this._data), nbItems, seed, this.verbose));
        return this;
    }
    removeDuplicates({ key, keepDuplicatesOnly = false, nbToKeep = 1, } = {}) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, removeDuplicates(cloneData(this._data), key, keepDuplicatesOnly, nbToKeep, this.verbose));
        return this;
    }
    keep({ key, value, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, keep(cloneData(this._data), key, value, this.verbose));
        return this;
    }
    exclude({ key, value, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, exclude(cloneData(this._data), key, value, this.verbose));
        return this;
    }
    keepNumbers({ key, keepNonNumbersOnly = false, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, keepNumbers(cloneData(this._data), key, keepNonNumbersOnly, this.verbose));
        return this;
    }
    keepDates({ key, keepNonDatesOnly = false, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, keepDates(cloneData(this._data), key, keepNonDatesOnly, this.verbose));
        return this;
    }
    keepStrings({ key, keepNonStringOnly = false, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, keepStrings(cloneData(this._data), key, keepNonStringOnly, this.verbose));
        return this;
    }
    // *** ANALYSIS METHODS *** //
    sortValues({ key, order = "ascending", locale, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, sortValues(cloneData(this._data), key, order, locale));
        return this;
    }
    addProportions({ method, key, keys, newKey, keyCategory, suffix, nbDigits = 2, nbTestedValues = 10000, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addProportions(cloneData(this._data), {
            method,
            keys,
            key,
            newKey,
            keyCategory,
            suffix,
            nbDigits,
            nbTestedValues,
            verbose: this.verbose,
        }));
        return this;
    }
    addVariation({ key, newKey, valueGenerator, order = undefined, firstValue = undefined, nbTestedValues = 10000, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addVariation(cloneData(this._data), key, newKey, valueGenerator, order, firstValue, nbTestedValues, this.verbose));
        return this;
    }
    summarize({ keyValue, keyCategory, summary, weight, nbTestedValues = 10000, nbDigits, } = {}) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, summarize(cloneData(this._data), keyValue, keyCategory, summary, weight, nbTestedValues, this.verbose, nbDigits));
        return this;
    }
    correlation({ keyX, keyY, keyCategory, nbDigits = 4, nbTestedValues = 10000, } = {}) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, correlation(cloneData(this._data), keyX, keyY, keyCategory, nbDigits, this.verbose, nbTestedValues));
        return this;
    }
    regression({ keyX, keyY, keyCategory, type = "linear", order, nbDigits = 4, nbTestedValues = 10000, } = {}) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, regression(cloneData(this._data), keyX, keyY, type, keyCategory, order, nbDigits, this.verbose, nbTestedValues));
        return this;
    }
    addQuantiles({ key, newKey, nbQuantiles, nbTestedValues = 10000, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addQuantiles(cloneData(this._data), key, newKey, nbQuantiles, nbTestedValues, this.verbose));
        return this;
    }
    addBins({ key, newKey, nbBins, nbTestedValues = 10000, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addBins(cloneData(this._data), key, newKey, nbBins, nbTestedValues, this.verbose));
        return this;
    }
    addOutliers({ key, newKey, nbTestedValues = 10000, }) {
        __classPrivateFieldGet(this, _SimpleData_instances, "m", _SimpleData_updateSimpleData).call(this, addOutliers(cloneData(this._data), key, newKey, nbTestedValues, this.verbose));
        return this;
    }
    // *** VISUALIZATION METHODS *** //
    getChart({ type, x, y, color, colorScale, trend = false, showTrendEquation = false, width, height, marginLeft, marginBottom, title, smallMultipleKey, smallMultipleWidth, smallMultipleHeight, }) {
        return getChart(cloneData(this._data), type, x, y, color, colorScale, trend, showTrendEquation, marginLeft, marginBottom, width, height, title, smallMultipleKey, smallMultipleWidth, smallMultipleHeight);
    }
    getCustomChart({ plotOptions }) {
        return getCustomChart(plotOptions);
    }
    // ** EXPORTING METHODS *** //
    clone() {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return new this.constructor({
            data: this._data,
            verbose: this.verbose,
            nbTableItemsToLog: this.nbTableItemsToLog,
        });
    }
    // No @logCall otherwise it's triggered everywhere, including in methods
    getData() {
        return this._data;
    }
    // No @logCall otherwise it's triggered everywhere, including in methods
    getFirst() {
        return this._data[0];
    }
    // No @logCall otherwise it's triggered everywhere, including in methods
    getLast() {
        return this._data[this._data.length - 1];
    }
    //No @logCall otherwise it's triggered everywhere, including in methods
    getLength() {
        return this._data.length;
    }
    //No @logCall otherwise it's triggered everywhere, including in methods
    getKeys() {
        return this._data.length > 0 ? Object.keys(this._data[0]) : [];
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getItem({ conditions, noWarning = false, }) {
        return getItem(this._data, conditions, noWarning);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getArray({ key }) {
        return getArray(this._data, key);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getDataAsArrays() {
        return getDataAsArrays(this._data);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getUniqueValues({ key }) {
        return getUniqueValues(this._data, key);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getMin({ key, nbDigits = undefined, nbTestedValues = 10000, type = "number", }) {
        return getMin(this._data, key, nbDigits, nbTestedValues, type, this.verbose);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getMax({ key, nbDigits = undefined, nbTestedValues = 10000, type = "number", }) {
        return getMax(this._data, key, nbDigits, nbTestedValues, type, this.verbose);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getMean({ key, nbDigits = 2, nbTestedValues = 10000, type = "number", }) {
        return getMean(this._data, key, nbDigits, nbTestedValues, type, this.verbose);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getMedian({ key, nbDigits = 2, nbTestedValues = 10000, type = "number", }) {
        return getMedian(this._data, key, nbDigits, nbTestedValues, type, this.verbose);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getSum({ key, nbDigits = 2, nbTestedValues = 10000, }) {
        return getSum(this._data, key, nbDigits, nbTestedValues, this.verbose);
    }
    // No @logCall for methods starting with get. It's not returning a simpleData class
    getQuantile({ key, quantile, nbDigits = 2, nbTestedValues = 10000, }) {
        return getQuantile(this._data, key, quantile, nbDigits, nbTestedValues, this.verbose);
    }
    getDuration() {
        return this.duration;
    }
    // *** LOGGING METHODS AND OTHERS *** //
    // No log call, otherwise the table is shown twice.
    showTable({ nbItemInTable = 5, } = {}) {
        showTable(this._data, nbItemInTable, true);
        return this;
    }
    showDuration() {
        log(`Total duration ${round(this.duration / 1000, 3)}.`);
        return this;
    }
}
_SimpleData_instances = new WeakSet(), _SimpleData_updateSimpleData = function _SimpleData_updateSimpleData(data) {
    this._data = data;
};
__decorate([
    asyncLogCall()
], SimpleData.prototype, "loadDataFromUrl", null);
__decorate([
    logCall()
], SimpleData.prototype, "describe", null);
__decorate([
    logCall()
], SimpleData.prototype, "checkValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "excludeMissingValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "formatAllKeys", null);
__decorate([
    logCall()
], SimpleData.prototype, "renameKey", null);
__decorate([
    logCall()
], SimpleData.prototype, "valuesToString", null);
__decorate([
    logCall()
], SimpleData.prototype, "valuesToInteger", null);
__decorate([
    logCall()
], SimpleData.prototype, "valuesToFloat", null);
__decorate([
    logCall()
], SimpleData.prototype, "valuesToDate", null);
__decorate([
    logCall()
], SimpleData.prototype, "datesToString", null);
__decorate([
    logCall()
], SimpleData.prototype, "roundValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "replaceValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "modifyValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "modifyItems", null);
__decorate([
    logCall()
], SimpleData.prototype, "excludeOutliers", null);
__decorate([
    logCall()
], SimpleData.prototype, "removeKey", null);
__decorate([
    logCall()
], SimpleData.prototype, "addKey", null);
__decorate([
    logCall()
], SimpleData.prototype, "addRank", null);
__decorate([
    logCall()
], SimpleData.prototype, "addItems", null);
__decorate([
    logCall()
], SimpleData.prototype, "mergeItems", null);
__decorate([
    logCall()
], SimpleData.prototype, "valuesToKeys", null);
__decorate([
    logCall()
], SimpleData.prototype, "keysToValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "selectKeys", null);
__decorate([
    logCall()
], SimpleData.prototype, "filterValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "filterItems", null);
__decorate([
    logCall()
], SimpleData.prototype, "pickRandomItems", null);
__decorate([
    logCall()
], SimpleData.prototype, "removeDuplicates", null);
__decorate([
    logCall()
], SimpleData.prototype, "keep", null);
__decorate([
    logCall()
], SimpleData.prototype, "exclude", null);
__decorate([
    logCall()
], SimpleData.prototype, "keepNumbers", null);
__decorate([
    logCall()
], SimpleData.prototype, "keepDates", null);
__decorate([
    logCall()
], SimpleData.prototype, "keepStrings", null);
__decorate([
    logCall()
], SimpleData.prototype, "sortValues", null);
__decorate([
    logCall()
], SimpleData.prototype, "addProportions", null);
__decorate([
    logCall()
], SimpleData.prototype, "addVariation", null);
__decorate([
    logCall()
], SimpleData.prototype, "summarize", null);
__decorate([
    logCall()
], SimpleData.prototype, "correlation", null);
__decorate([
    logCall()
], SimpleData.prototype, "regression", null);
__decorate([
    logCall()
], SimpleData.prototype, "addQuantiles", null);
__decorate([
    logCall()
], SimpleData.prototype, "addBins", null);
__decorate([
    logCall()
], SimpleData.prototype, "addOutliers", null);
__decorate([
    logCall()
], SimpleData.prototype, "getChart", null);
__decorate([
    logCall()
], SimpleData.prototype, "getCustomChart", null);
__decorate([
    logCall()
], SimpleData.prototype, "clone", null);
//# sourceMappingURL=SimpleData.js.map